import BossModel from "@/models/BossModel";
import MediaLibraryItem from '@/models/Media/Library/Item/MediaLibraryItem';
import {saveAs} from 'file-saver';

export default class MediaLibraryFolder extends BossModel {

    /**
     *
     * @type {string}
     */
    static apiUrl = 'media-library-folder';

    /**
     *
     * @type {string}
     */
    static entity = 'mediaLibraryFolders';

    static fields() {
        return {
            id: this.number(null).nullable(),
            name: this.attr(null),
            media_library_folder_parent_id: this.number(null).nullable(),
            deleted_at: this.attr(null),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            mediaLibraryItems: this.hasMany(MediaLibraryItem, 'media_library_folder_id'),
            childFolders: this.hasMany(MediaLibraryFolder, 'media_library_folder_parent_id'),
            parentFolder: this.belongsTo(MediaLibraryFolder, 'media_library_folder_parent_id'),
        }
    }

    static getHome() {
        return this.getRelated('childFolders').find(folder => {
            return folder.name === 'Home'
        });
    }

    static multiUpdateParentFolder(folderIds, folderId) {
        return this.api().action('multi-update/parent-folder').update({
            folderIds: folderIds,
            folderId: folderId
        }, false);
    }

    /**
     *
     * @param id
     * @returns {*}
     */
    static loadWithParents(id) {
        return this.api()
            .with([
                'parentFolders',
                'childFolders',
            ])
            .find(id);
    }

    /**
     *
     * @param name
     * @returns {Promise<TResult>}
     */
    static loadWithParentsByName(name) {
        this.loading = true;
        return this.api().action(name + '/fetch').with([
            'childFolders',
            'mediaLibraryItems',
            'mediaLibraryItems.mediaLibraryTags',
            'mediaLibraryItems.mediaLibraryVideo',
            'mediaLibraryItems.mediaLibraryImage',
            'mediaLibraryItems.mediaLibraryDocument',
            'mediaLibraryItems.mediaLibraryPdf',
            'mediaLibraryItems.mediaLibraryVector',
        ])
            .appended(['imageCount'])
            .getSingle();
    }

    /**
     *
     * @param name
     * @param parentId
     * @returns {Promise<AxiosResponse<T>>}
     */
    static create(name, parentId = null) {
        return this.api().create({name: name, media_library_folder_parent_id: parentId});
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    update() {
        return this.api().update();
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    restore() {
        return this.constructor.api().action('restore/' + this.id)
            .getSingle();
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    destroy() {
        return this.api().delete();
    }

    /**
     *
     * @param folders
     * @returns {Promise<AxiosResponse<T>>}
     */
    static deleteFolders(folders) {
        return this.api().action('delete-folders').update({'folders': folders});
    }

    /**
     *
     * @param term
     * @param tags
     * @returns {*}
     */
    static search(term) {
        return this.api().action('search')
            .where('term', term)
            .limit(30)
            .with([
                'childFolders',
                'mediaLibraryItems.mediaLibraryTags',
                'mediaLibraryItems.mediaLibraryVideo',
                'mediaLibraryItems.mediaLibraryImage',
                'mediaLibraryItems.mediaLibraryDocument',
                'mediaLibraryItems.mediaLibraryPdf',
                'mediaLibraryItems.mediaLibraryVector',
            ])
            .get();
    }

    /**
     *
     * @returns {boolean}
     */
    hasParent() {
        return this.getRelated('parent') !== null;
    }

    /**
     *
     * @returns {boolean}
     */
    hasChildren() {
        return this.getRelated('childFolders').length > 0
    }

    /**
     *
     * @returns {boolean|*}
     */
    ParentFolder() {
        return this.getRelated('parent');
    }

    /**
     *
     * @param parents
     * @returns {*[]}
     */
    ParentFolders(parents = []) {
        if (this.hasParent() === true) {
            let parent = this.ParentFolder();
            parents.push(parent);
            return parent.ParentFolders(parents);
        } else if (this.name !== 'Home') {
            parents.push(this);
        }

        return parents.reverse();
    }

    /**
     * Purely for ease of use when finding the type of item.
     * @returns {string}
     */
    getChildType() {
        return 'folder';
    }

    /**
     *
     * @returns {*}
     */
    ChildFolders() {
        return this.getRelated('childFolders');
    }

    /**
     *
     * @returns {*}
     */
    Items() {
        return this.getRelated('mediaLibraryItems');
    }

    /**
     *
     * @returns {*}
     */
    ActiveItems() {
        return this.getRelated('mediaLibraryItems', function (item) {
            return item.getChild().is_usable === undefined || item.getChild().is_usable;
        });
    }

    /**
     *
     * @param folderId
     * @returns {Promise<AxiosResponse<T>>}
     */
    moveToFolder(folderId) {
        this.media_library_folder_parent_id = folderId;
        return this.update();
    }

    /**
     *
     * @param context
     * @returns {PromiseLike<any> | Promise<any> | *}
     */
    download() {
        return this.constructor.api().action(this.id + '/download').getBlob().then((response) => {
            return saveAs(response, 'media-library-items.zip');
        });
    }

    /**
     *
     * @param context
     * @returns {*}
     */
    deleteFolder(context) {
        if (this.child_folder_count > 0 || this.ActiveItems().length > 0) {
            context.$toasted.error('Deze map is niet leeg, verwijderen is niet mogelijk.')
            return;
        }
        if (this.media_library_folder_parent_id == null) {
            context.$toasted.error('De home map mag niet verwijderd worden.')
            return;
        }

        return context.$emit('deleteFolder', this);
    }

}
